import * as React from "react";
import { useMemo, useState } from "react";
import { ArrowRight, ChevronDown, ListFilter, X } from "lucide-react";
import { cn } from "../../lib/utils";

// Shadcn components
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// Types
import { TagFilter } from "../../pages/Clusters/types";
import { Tag, TagOption, TagValues } from "../../crud/tags/types";
import { BadgeWithClose } from "./badge-with-close";

const ANY_OPTION = {
  label: "*",
  value: "*",
};

type MultiSelectFilterProps = {
  // Data
  tags?: Tag[];
  allTagValues?: TagValues;

  // Current state
  selectedFilters: TagFilter[];
  inputValue: string;

  // Change handlers
  onFiltersChange: (filters: TagFilter[]) => void;
  onInputChange: (value: string) => void;

  // UI props
  placeholder?: string;
  label?: string;
  className?: string;
};

export const MultiSelectFilter = ({
  tags = [],
  allTagValues = {},
  selectedFilters,
  inputValue,
  onFiltersChange,
  onInputChange,
  placeholder = "Filter by tags and/or by cluster name...",
  label,
  className,
}: MultiSelectFilterProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState<TagOption | null>(
    null,
  );

  // Format tag filters (add ":*" for display if needed)
  const formattedTagFilters = selectedFilters
    .map((filter) => (!filter?.includes(":") ? `${filter}:*` : filter))
    .filter(Boolean) as string[];

  // Determine if we're entering a tag value (after ":")
  const isEnteringValue = inputValue.includes(":");
  const currentTagName = isEnteringValue ? inputValue.split(":")[0] : "";
  const currentTagValue = isEnteringValue ? inputValue.split(":")[1] : "";

  // Generate options based on current input state
  const options = useMemo(() => {
    const opts: TagOption[] = [];

    if (isEnteringValue) {
      // Show tag values for the current tag
      const tagValues = allTagValues?.[currentTagName] || [];
      const existingTag = tags?.find((tag) => tag.label === currentTagName);

      // Always add "match any" option first
      opts.push({
        label: ANY_OPTION.label,
        value: `${currentTagName}:${ANY_OPTION.value}`,
        systemTag: existingTag?.systemTag,
      });

      // Add specific values if they exist
      if (tagValues.length > 0) {
        tagValues.forEach((value) => {
          opts.push({
            label: value,
            value: `${currentTagName}:${value}`,
            systemTag: existingTag?.systemTag,
          });
        });
      }
    } else {
      // Show available tag names
      tags.forEach((tag) => {
        // Don't show tags that are already selected (unless entering a different value)
        const isAlreadySelected = formattedTagFilters.some((filter) =>
          filter.startsWith(`${tag.label}:`),
        );

        if (!isAlreadySelected) {
          opts.push({
            label: tag.label,
            value: tag.label,
            systemTag: tag.systemTag,
          });
        }
      });
    }

    return opts;
  }, [
    tags,
    allTagValues,
    currentTagName,
    isEnteringValue,
    formattedTagFilters,
  ]);

  // Filter options based on current input
  const filteredOptions = useMemo(() => {
    const searchValue = isEnteringValue ? currentTagValue : inputValue;

    // If no search value or we're just starting to enter a value (empty after ":"), show all options
    if (!searchValue || (isEnteringValue && searchValue === "")) {
      return options;
    }

    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );

    return filtered;
  }, [options, inputValue, isEnteringValue, currentTagValue]);

  // Handle option selection
  const handleOptionSelect = (option: TagOption) => {
    if (isEnteringValue) {
      // Selecting a tag value - replace any existing filter for this tag
      const newFilter = option.value;
      const tagName = currentTagName;
      // Remove any existing filters for this tag name
      const filteredExisting = selectedFilters.filter((filter) => {
        const formatted = !filter?.includes(":") ? `${filter}:*` : filter;
        return !formatted.startsWith(`${tagName}:`);
      });
      const newFilters = [...filteredExisting, newFilter];
      onFiltersChange(newFilters);
      onInputChange("");
      setOpen(false);
    } else {
      // Selecting a tag name - start entering value mode (don't create filter yet)
      // FIXME this gets applied as a cluster name filter
      onInputChange(`${option.value}:`);

      // Keep popover open so they can select a value
      setTimeout(() => setOpen(true), 0);
    }
  };

  // Handle free text input (cluster name search)
  const handleFreeTextInput = () => {
    if (inputValue && !isEnteringValue) {
      // For cluster name searches, update the clusterNameFilter instead of adding to tagFilters
      // This keeps the search state alive and doesn't interfere with the filtering logic
      onInputChange(inputValue); // This updates clusterNameFilter in the parent
      setOpen(false);
    }
  };

  // Remove a selected filter
  const removeFilter = (filterToRemove: string) => {
    const newFilters = selectedFilters.filter((filter) => {
      const formatted = !filter?.includes(":") ? `${filter}:*` : filter;
      return formatted !== filterToRemove;
    });
    onFiltersChange(newFilters);
  };

  // Format filter display
  const formatFilterLabel = (filter: string) => {
    if (filter.endsWith(":*")) {
      return filter.slice(0, -2); // Remove ":*"
    }
    return filter;
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && inputValue && !isEnteringValue) {
      // Only handle Enter for cluster name if no tag is highlighted
      if (!highlightedOption) {
        event.preventDefault();
        handleFreeTextInput();
      }
      // If a tag is highlighted, let the Command component handle the selection
    } else if (event.key === "Tab" && highlightedOption) {
      event.preventDefault();
      handleOptionSelect(highlightedOption);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          onClick={() => setOpen(true)}
          className={cn(
            "min-h-[44px] w-full min-w-[280px] cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm transition-[color,background-color,border-color] shadow-xs",
            "hover:bg-primary-muted hover:border-primary/30 hover:text-primary",
            "grid grid-cols-[auto_1fr_auto] items-start gap-2",
          )}
        >
          {/* Left column: ListFilter icon */}
          <ListFilter className="size-4 text-muted-foreground shrink-0 mt-1.25" />
          {/* Middle column: Badges or placeholder */}
          <div className="min-h-[22px] flex flex-wrap gap-1">
            {/* Show current cluster name filter if no tag filters */}
            {formattedTagFilters.length === 0 &&
              inputValue &&
              !isEnteringValue && (
                <BadgeWithClose
                  variant="default"
                  className="text-xs gap-1 pr-0.5"
                  onClose={() => onInputChange("")}
                >
                  Cluster name: {inputValue}
                </BadgeWithClose>
              )}
            {formattedTagFilters.length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-0.5">
                {formattedTagFilters.map((filter, index) => {
                  const tag = tags.find((t) =>
                    filter.startsWith(`${t.label}:`),
                  );
                  return (
                    <BadgeWithClose
                      key={`${filter}-${index}`}
                      variant={tag?.systemTag ? "outline" : "default"}
                      className="text-xs gap-1 pr-0.5 cursor-pointer"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        // Extract tag name from filter (e.g., "environment" from "environment:production")
                        const tagName = filter.includes(":")
                          ? filter.split(":")[0]
                          : filter;
                        onInputChange(`${tagName}:`);
                        setOpen(true);
                      }}
                      onClose={() => removeFilter(filter)}
                    >
                      {formatFilterLabel(filter)}
                    </BadgeWithClose>
                  );
                })}
              </div>
            ) : null}

            {/* Show placeholder only when no filters and no input */}
            {formattedTagFilters.length === 0 && !inputValue && (
              <span className="text-muted-foreground select-none text-left self-center mt-0.5">
                {placeholder}
              </span>
            )}
          </div>

          {/* Right column: Clear button or chevron */}
          <div className="mt-0.75">
            {formattedTagFilters.length > 0 || inputValue ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onFiltersChange([]);
                  onInputChange("");
                  setOpen(false);
                }}
                className="h-5 w-4 p-0 shrink-0 hover:bg-transparent text-gray-500 hover:text-foreground"
              >
                <X className="size-4" />
              </Button>
            ) : (
              <ChevronDown className="size-4 text-gray-500 mt-0.75" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command
            onKeyDown={handleKeyDown}
            filter={(value, search) => {
              // Custom filter - when entering values, show all options initially
              if (isEnteringValue && currentTagValue === "") {
                return 1; // Show all options
              }
              // Otherwise use default filtering
              if (
                search &&
                value.toLowerCase().includes(search.toLowerCase())
              ) {
                return 1;
              }
              return 0;
            }}
          >
            <CommandInput
              value={inputValue}
              onValueChange={onInputChange}
              placeholder={
                isEnteringValue
                  ? `Select value for "${currentTagName}"...`
                  : "Search tags or type cluster name..."
              }
              className="h-9"
              onKeyDown={handleKeyDown}
            />
            <CommandList maxHeight="75vh">
              {/* Show cluster name option when typing free text - always visible */}
              <CommandGroup
                heading={
                  !isEnteringValue
                    ? "Type to filter on cluster name"
                    : "Cluster name"
                }
              >
                {!isEnteringValue && inputValue && (
                  <CommandItem
                    value={`cluster-name-${inputValue}`}
                    onSelect={() => handleFreeTextInput()}
                    className="px-2 py-1.5 cursor-pointer"
                  >
                    <Badge variant="default" className="text-xs">
                      Cluster name: {inputValue}
                    </Badge>
                  </CommandItem>
                )}
              </CommandGroup>

              <CommandSeparator />

              {/* Show available tag options */}
              {filteredOptions.length > 0 && (
                <CommandGroup
                  heading={
                    isEnteringValue
                      ? `Values for "${currentTagName}"`
                      : "Available Tags"
                  }
                >
                  {filteredOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => handleOptionSelect(option)}
                      onMouseEnter={() => setHighlightedOption(option)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Badge
                        variant={option.systemTag ? "outline" : "default"}
                        className="text-xs"
                      >
                        {option.label}
                      </Badge>

                      {/* Special formatting for "match any" option */}
                      {option.label === "*" && (
                        <span className="text-xs text-muted-foreground italic">
                          match any value
                        </span>
                      )}

                      {/* Show arrow when highlighted and not entering value */}
                      {highlightedOption?.value === option.value &&
                        !isEnteringValue && (
                          <ArrowRight className="ml-auto size-4 text-muted-foreground" />
                        )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              <CommandEmpty>
                {isEnteringValue ? "No values found." : "No tags found."}
              </CommandEmpty>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export type { MultiSelectFilterProps };
